/* ============================================
   CostosEstimados — Editable Direct Costs List
   Used in NuevaCotizacion right panel for
   internal cost analysis (not visible to client)
   ============================================ */
import type { CategoriaCosto } from '@/lib/types'
import { r2, formatCurrency } from '@/lib/utils'
import { IconTrash, IconPlus } from '@/components/icons/Icons'

const CATEGORIAS: { value: CategoriaCosto; label: string }[] = [
  { value: 'freelance', label: 'Freelance' },
  { value: 'herramienta', label: 'Herramienta' },
  { value: 'licencia', label: 'Licencia' },
  { value: 'infraestructura', label: 'Infraestructura' },
  { value: 'otro', label: 'Otro' },
]

/** Local working type — omits DB fields not needed during editing */
export interface CostoEstimadoLocal {
  descripcion: string
  monto: number
  categoria: CategoriaCosto
}

interface CostosEstimadosProps {
  costos: CostoEstimadoLocal[]
  onUpdate: (costos: CostoEstimadoLocal[]) => void
  moneda: string
}

export default function CostosEstimados({ costos, onUpdate, moneda }: CostosEstimadosProps) {
  const totalCostos = r2(costos.reduce((s, c) => s + c.monto, 0))

  const updateRow = (idx: number, field: keyof CostoEstimadoLocal, value: string | number) => {
    onUpdate(costos.map((c, i) => {
      if (i !== idx) return c
      if (field === 'monto') return { ...c, monto: parseFloat(String(value)) || 0 }
      return { ...c, [field]: value }
    }))
  }

  const removeRow = (idx: number) => onUpdate(costos.filter((_, i) => i !== idx))

  const addRow = () => onUpdate([...costos, { descripcion: '', monto: 0, categoria: 'otro' }])

  return (
    <div className="costos-estimados">
      <div className="costos-estimados__header">
        <h4 className="costos-estimados__title">Costos Directos</h4>
        <span className="costos-estimados__count">{costos.length} item{costos.length !== 1 ? 's' : ''}</span>
      </div>

      {costos.length === 0 ? (
        <p className="costos-estimados__empty">Sin costos registrados</p>
      ) : (
        <div className="costos-estimados__list">
          <div className="costos-estimados__row costos-estimados__row--header">
            <span>Descripcion</span>
            <span>Monto</span>
            <span>Categoria</span>
            <span></span>
          </div>
          {costos.map((c, i) => (
            <div className="costos-estimados__row" key={i}>
              <input
                className="form-input"
                value={c.descripcion}
                onChange={e => updateRow(i, 'descripcion', e.target.value)}
                placeholder="Ej: Diseñador freelance"
              />
              <input
                className="form-input"
                type="number"
                step="0.01"
                value={c.monto}
                onChange={e => updateRow(i, 'monto', e.target.value)}
              />
              <select
                className="form-select"
                value={c.categoria}
                onChange={e => updateRow(i, 'categoria', e.target.value)}
              >
                {CATEGORIAS.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <button className="costos-estimados__remove" onClick={() => removeRow(i)} type="button">
                <IconTrash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button className="btn btn-secondary btn-sm costos-estimados__add" onClick={addRow} type="button">
        <IconPlus size={14} /> Agregar costo
      </button>

      {costos.length > 0 && (
        <div className="costos-estimados__footer">
          <span>Total Costos</span>
          <strong>{formatCurrency(totalCostos, moneda)}</strong>
        </div>
      )}
    </div>
  )
}
